import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  TableSortLabel,
  TablePagination,
  useTheme,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import {
  useGetPlanList,
  useGetPlanPricing,
  useCreatePlanPricing,
} from "../../hooks/api/usePlan";
import { useGetBillingCycleList } from "../../hooks/api/useBillingCycle";
import type { Plan } from "../../Graphql/types/plan";
import type { PlanPricingItem } from "../../Graphql/types/plan";
import type { BillingCycle } from "../../Graphql/types/billingCycle";
import { getPlanPricingDetailPath } from "../../constants/routes";
import { adminSurface, adminTableRowSx } from "../../styles/adminUiTokens";
import MembershipColumnFilterField from "./MembershipColumnFilterField";
import {
  compareSortValues,
  hasActiveFilters,
  matchesStatusFilter,
  matchesTextFilter,
  type SortOrder,
} from "./utils/membershipTableUtils";
import {
  ADMIN_PLAN_PRICING_COUNTRIES,
  currencyForAdminCountry,
} from "./constants/adminPlanPricingCountries";

type OrderBy =
  | "planName"
  | "billingCycleCode"
  | "countryCode"
  | "price"
  | "active";
type Order = SortOrder;

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

type PricingFieldFilters = {
  planName: string;
  billingCycleCode: string;
  countryCode: string;
  price: string;
  status: string;
};

const EMPTY_PRICING_FILTERS: PricingFieldFilters = {
  planName: "",
  billingCycleCode: "",
  countryCode: "",
  price: "",
  status: "",
};

function resolveCountryCode(p: PlanPricingItem): string {
  return (
    p.countryCode ??
    ADMIN_PLAN_PRICING_COUNTRIES.find((c) => c.currency === p.priceCurrency)
      ?.code ??
    ""
  );
}

function formatPrice(p: PlanPricingItem): string {
  return `${p.priceAmount ?? ""} ${p.priceCurrency ?? ""}`.trim();
}

function getSortValue(p: PlanPricingItem, field: OrderBy): string | number {
  if (field === "planName") return p.planName ?? "";
  if (field === "billingCycleCode") return p.billingCycleCode ?? "";
  if (field === "countryCode") return resolveCountryCode(p);
  if (field === "price") return p.priceAmount ?? 0;
  return p.isActive ? 1 : 0;
}

function filterAndSort(
  list: PlanPricingItem[],
  filters: PricingFieldFilters,
  orderBy: OrderBy,
  order: Order,
): PlanPricingItem[] {
  const filtered = list.filter(
    (p) =>
      matchesTextFilter(p.planName ?? "", filters.planName) &&
      matchesTextFilter(p.billingCycleCode ?? "", filters.billingCycleCode) &&
      matchesTextFilter(resolveCountryCode(p), filters.countryCode) &&
      matchesTextFilter(formatPrice(p), filters.price) &&
      matchesStatusFilter(p.isActive, filters.status),
  );
  return filtered.sort((a, b) =>
    compareSortValues(
      getSortValue(a, orderBy),
      getSortValue(b, orderBy),
      order,
    ),
  );
}

const PlanPricingListTable: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [rows, setRows] = useState<PlanPricingItem[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [billingCycles, setBillingCycles] = useState<BillingCycle[]>([]);
  const [fieldFilters, setFieldFilters] = useState<PricingFieldFilters>(
    EMPTY_PRICING_FILTERS,
  );
  const [orderBy, setOrderBy] = useState<OrderBy>("planName");
  const [order, setOrder] = useState<Order>("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addPlanId, setAddPlanId] = useState("");
  const [addBillingCycleId, setAddBillingCycleId] = useState("");
  const [addAmount, setAddAmount] = useState("");
  const [addCountryCode, setAddCountryCode] = useState("RW");
  const [addCurrency, setAddCurrency] = useState("RWF");

  const getPlanList = useGetPlanList();
  const getPlanPricing = useGetPlanPricing();
  const createPlanPricing = useCreatePlanPricing();
  const getBillingCycleList = useGetBillingCycleList();

  useEffect(() => {
    getPlanList
      .mutateAsync({ activeOnly: false })
      .then((res) => {
        setPlans(res.listPlans?.planList ?? []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getBillingCycleList
      .mutateAsync({ activeOnly: false })
      .then((res) => {
        setBillingCycles(res.listBillingCycles?.billingCycleList ?? []);
      })
      .catch(() => {});
  }, []);

  const fetchAllPlanPricings = async (planList: Plan[]) => {
    const all: PlanPricingItem[] = [];
    for (const plan of planList) {
      try {
        const res = await getPlanPricing.mutateAsync({ planId: plan.planId });
        all.push(...(res.getPlanPricing?.planPricingList ?? []));
      } catch {
        // skip
      }
    }
    setRows(all);
  };

  useEffect(() => {
    if (plans.length === 0) {
      setRows([]);
      return;
    }
    fetchAllPlanPricings(plans);
  }, [plans.length]);

  const sorted = useMemo(
    () => filterAndSort(rows, fieldFilters, orderBy, order),
    [rows, fieldFilters, orderBy, order],
  );

  const updateFieldFilter = (key: keyof PricingFieldFilters, value: string) => {
    setFieldFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleSort = (field: OrderBy) => {
    const isAsc = orderBy === field && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleAddSuccess = () => {
    setAddModalOpen(false);
    setAddPlanId("");
    setAddBillingCycleId("");
    setAddAmount("");
    setAddCountryCode("RW");
    setAddCurrency("RWF");
    if (plans.length > 0) fetchAllPlanPricings(plans);
  };

  const handleCreate = () => {
    if (!addPlanId || !addBillingCycleId || !addAmount.trim()) return;
    const amount = parseFloat(addAmount);
    if (Number.isNaN(amount)) return;
    createPlanPricing
      .mutateAsync({
        planId: addPlanId,
        billingCycleId: addBillingCycleId,
        priceAmount: amount,
        priceCurrency: addCurrency || undefined,
        countryCode: addCountryCode,
      })
      .then(() => handleAddSuccess())
      .catch(() => {});
  };

  const isLoading = plans.length === 0 && getPlanList.isPending;
  const isEmpty = !isLoading && rows.length === 0;
  const noSearchResults = !isEmpty && sorted.length === 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper elevation={0} sx={{ ...adminSurface, width: "100%" }}>
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {hasActiveFilters(fieldFilters) && (
            <Button
              size="small"
              color="inherit"
              onClick={() => setFieldFilters(EMPTY_PRICING_FILTERS)}
            >
              Clear column filters
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            size="medium"
            startIcon={<Add />}
            onClick={() => setAddModalOpen(true)}
          >
            Add
          </Button>
        </Box>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor:
                    theme.palette.mode === "light" ? "grey.50" : "grey.900",
                }}
              >
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "planName"}
                    direction={orderBy === "planName" ? order : "asc"}
                    onClick={() => handleSort("planName")}
                  >
                    Plan
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "billingCycleCode"}
                    direction={orderBy === "billingCycleCode" ? order : "asc"}
                    onClick={() => handleSort("billingCycleCode")}
                  >
                    Billing cycle
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "countryCode"}
                    direction={orderBy === "countryCode" ? order : "asc"}
                    onClick={() => handleSort("countryCode")}
                  >
                    Country
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "price"}
                    direction={orderBy === "price" ? order : "asc"}
                    onClick={() => handleSort("price")}
                  >
                    Price
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "active"}
                    direction={orderBy === "active" ? order : "asc"}
                    onClick={() => handleSort("active")}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 48, py: 2 }} />
              </TableRow>
              <TableRow
                sx={{
                  bgcolor:
                    theme.palette.mode === "light" ? "grey.100" : "grey.800",
                }}
              >
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.planName}
                    onChange={(v) => updateFieldFilter("planName", v)}
                    aria-label="Filter by plan"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.billingCycleCode}
                    onChange={(v) => updateFieldFilter("billingCycleCode", v)}
                    aria-label="Filter by billing cycle"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.countryCode}
                    onChange={(v) => updateFieldFilter("countryCode", v)}
                    aria-label="Filter by country"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.price}
                    onChange={(v) => updateFieldFilter("price", v)}
                    aria-label="Filter by price"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.status}
                    onChange={(v) => updateFieldFilter("status", v)}
                    placeholder="Active…"
                    aria-label="Filter by status"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton variant="text" width="40%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="30%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={60} />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                ))
              ) : isEmpty ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 6, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No plan pricings found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : noSearchResults ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 6, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No plan pricings match the current column filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((p, index) => (
                  <TableRow
                    key={p.planPricingId}
                    hover
                    sx={{
                      ...adminTableRowSx(index),
                      cursor: "pointer",
                      "&:hover .row-arrow": { opacity: 1 },
                    }}
                    onClick={() =>
                      navigate(
                        getPlanPricingDetailPath(p.planId, p.planPricingId),
                      )
                    }
                  >
                    <TableCell sx={{ py: 2, fontWeight: 500 }}>
                      {p.planName}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 2,
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                      }}
                    >
                      {p.billingCycleCode}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {resolveCountryCode(p) || "—"}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {p.priceAmount} {p.priceCurrency}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={p.isActive ? "Active" : "Inactive"}
                        size="small"
                        color={p.isActive ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, width: 48 }}>
                      <ArrowForwardIos
                        className="row-arrow"
                        sx={{ fontSize: 14, color: "primary.main", opacity: 0 }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && !isEmpty && sorted.length > 0 && (
          <TablePagination
            component="div"
            count={sorted.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Rows:"
            sx={{ borderTop: 1, borderColor: "divider" }}
          />
        )}
      </Paper>

      <Dialog
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add plan pricing</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Plan</InputLabel>
              <Select
                value={addPlanId}
                label="Plan"
                onChange={(e) => setAddPlanId(e.target.value)}
              >
                {plans.map((p) => (
                  <MenuItem key={p.planId} value={p.planId}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Billing cycle</InputLabel>
              <Select
                value={addBillingCycleId}
                label="Billing cycle"
                onChange={(e) => setAddBillingCycleId(e.target.value)}
              >
                {billingCycles.map((bc) => (
                  <MenuItem key={bc.billingCycleId} value={bc.billingCycleId}>
                    {bc.label} ({bc.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Country</InputLabel>
              <Select
                value={addCountryCode}
                label="Country"
                onChange={(e) => {
                  const code = e.target.value;
                  setAddCountryCode(code);
                  setAddCurrency(currencyForAdminCountry(code) ?? "RWF");
                }}
              >
                {ADMIN_PLAN_PRICING_COUNTRIES.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.label} ({c.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Amount"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={addAmount}
              onChange={(e) => setAddAmount(e.target.value)}
            />
            <TextField
              size="small"
              label="Currency"
              value={addCurrency}
              InputProps={{ readOnly: true }}
              helperText="Set automatically from country"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={
              !addPlanId ||
              !addBillingCycleId ||
              !addAmount.trim() ||
              createPlanPricing.isPending
            }
          >
            {createPlanPricing.isPending ? "Creating…" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlanPricingListTable;
